import { useId } from "react";
import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { Info } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from "recharts";
import dayjs from "dayjs";
import { useTranslation } from "../../../language";

const TOTAL_DAYS = 14;
const DATE_FORMAT = "YYYY-MM-DD";
// Asymptotic cap: percent approaches MAX_PERCENT but mathematically never reaches it,
// no matter how many days elapse, so the bar always stays below 97%.
const MAX_PERCENT = 96;
const PERCENT_RATE = 2;

const GAUGE_HEIGHT = 14;
const GAUGE_CONTAINER_HEIGHT = 36;
const GAUGE_AXIS_TICKS = [0, 25, 50, 75, 100];

function cardSx(theme, accentColor) {
  return {
    border: '1px solid',
    borderRadius: 4,
    overflow: 'hidden',
    borderLeft: `10px solid ${accentColor}`,
    ...(theme.palette.mode === 'light'
      ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
      : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
  };
}

export default function ResultsProgressBar({ visitDate, onInfoClick }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const gradientId = `results-progress-gradient-${useId()}`;

  if (!visitDate) return null;

  const daysSince = dayjs().startOf('day').diff(dayjs(visitDate, DATE_FORMAT).startOf('day'), 'day');
  const effectiveDays = Math.max(0, daysSince);
  const cappedDays = Math.min(effectiveDays, TOTAL_DAYS);
  const percent = Math.max(1, Math.round(MAX_PERCENT * (1 - Math.exp(-PERCENT_RATE * effectiveDays / TOTAL_DAYS))));
  const isRunningLong = daysSince > TOTAL_DAYS;
  const accentColor = theme.palette.tertiary.C300;
  const trackColor = theme.palette.mode === 'light' ? theme.palette.grey.C100 : theme.palette.greydark.C900;

  return (
    <Box sx={{ ...cardSx(theme, accentColor), p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="caption" fontWeight={700} letterSpacing={0.6} color="text.secondary">
            {t("onb_results_wait_eyebrow")}
          </Typography>
          {onInfoClick && (
            <IconButton size="small" onClick={onInfoClick} aria-label={t("onb_results_wait_lab_header")}>
              <Info size={18} color={theme.palette.text.secondary} />
            </IconButton>
          )}
        </Stack>
        <Typography variant="h5" fontWeight={700}>
          {percent}%
        </Typography>
      </Stack>

      <ResponsiveContainer width="100%" height={GAUGE_CONTAINER_HEIGHT}>
        <BarChart
          layout="vertical"
          data={[{ name: 'progress', value: percent }]}
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={theme.palette.tertiary.C300} />
              <stop offset="100%" stopColor={theme.palette.quartiary.C300} />
            </linearGradient>
          </defs>
          <XAxis
            type="number"
            domain={[0, 100]}
            ticks={GAUGE_AXIS_TICKS}
            tickFormatter={(v) => `${v}%`}
            axisLine={false}
            tickLine={{ stroke: trackColor }}
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
          />
          <YAxis type="category" dataKey="name" hide />
          <Bar
            dataKey="value"
            fill={`url(#${gradientId})`}
            background={{ fill: trackColor, radius: GAUGE_HEIGHT / 2 }}
            radius={GAUGE_HEIGHT / 2}
            isAnimationActive
          />
        </BarChart>
      </ResponsiveContainer>

      <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ display: 'block', mt: 1.5 }}>
        {isRunningLong
          ? t("onb_results_wait_running_long")
          : `${t("onb_results_wait_day_prefix")} ${cappedDays} ${t("onb_results_wait_day_of")}`}
      </Typography>
    </Box>
  );
}
